import { createContext, useContext } from "react";

export interface PortalLoginEmailOption {
  /** Partially obscured address, safe to render before verification. */
  maskedEmail: string;
  /** Opaque server-issued handle for the real address. */
  token: string;
}

export interface PortalLoginChallenge {
  challengeId: string;
  emailOptions: PortalLoginEmailOption[];
  /**
   * Masked address the code was sent to, shown on the verify step. Set from the
   * response on the single-email path and from the chosen option on the
   * multi-email path; null until an email is settled.
   */
  selectedMaskedEmail: string | null;
  /**
   * The verification code, echoed back by the API only in internal and demo
   * environments so testers can finish the flow without the applicant's inbox.
   * Always null against production.
   */
  demoCode: string | null;
}

export interface PortalLoginContextValue {
  challenge: PortalLoginChallenge | null;
  setChallenge: (challenge: PortalLoginChallenge) => void;
}

/**
 * Holds the in-flight identity challenge across the login steps.
 *
 * This lives in memory on the `/portal/login` layout route rather than in the
 * URL or router state so the challenge id is never linkable or restorable. A
 * refresh drops it, which is what we want: `PortalLoginGuard` then sends the
 * user back to the start rather than onto a step that can no longer succeed.
 */
export const PortalLoginContext = createContext<PortalLoginContextValue | null>(null);

export function usePortalLogin(): PortalLoginContextValue {
  const context = useContext(PortalLoginContext);

  if (!context) {
    throw new Error("usePortalLogin must be used within the portal login layout route");
  }

  return context;
}
